import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { InventoryManager } from '../systems/InventoryManager';
import { ITEM_DEFINITIONS, SLOT_LABELS, CONSUMABLE_DEFINITIONS, CONSUMABLE_LOOT } from '../data/items';
import { ItemDefinition } from '../types/Item.types';
import { SfxManager } from '../utils/SfxManager';
import { RARITY_COLORS } from '../ui/InventoryUI';
import { ensureTouchTarget } from '../ui/MobileTouchUI';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE MARCHAND ===
// Le joueur peut acheter des items avec son or.
// Utilise InventoryManager comme source de vérité unique.

// Prix par rareté
const PRICES: Record<string, number> = {
  common: 10,
  uncommon: 20,
  rare: 35,
};

// Coût du soin
const HEAL_COST = 12;
const HEAL_PERCENT = 0.4;

export class ShopScene extends Phaser.Scene {
  private run!: RunManager;
  private inv!: InventoryManager;
  private sfx = new SfxManager();
  private shopItems: ItemDefinition[] = [];

  constructor() {
    super({ key: 'ShopScene' });
  }

  init(data: { run?: RunManager; shopItems?: ItemDefinition[] }): void {
    this.run = data?.run ?? new RunManager();
    this.inv = this.run.getInventory();
    // Conserver les items du magasin au retour de l'équipement
    this.shopItems = data?.shopItems ?? this.generateShop();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  /** Génère 4 items aléatoires à vendre */
  private generateShop(): ItemDefinition[] {
    const allItems = Object.values(ITEM_DEFINITIONS);
    const shuffled = [...allItems].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 4);
  }

  private drawUI(): void {
    this.children.removeAll(true);

    // Fond
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e);

    // Titre
    this.add.text(GAME_CX, 35, '🏪 MARCHAND', {
      fontSize: '22px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5);

    // Or et capacité du joueur (utilise le vrai inventaire)
    // Sur mobile, on coupe en 2 lignes pour éviter le débordement
    const _mobShop = GAME_W < 500;
    if (_mobShop) {
      this.add.text(GAME_CX, 60, `💰 ${this.run.getGold()} Or`, {
        fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
      this.add.text(GAME_CX, 78, `🎒 ${this.inv.getTransportCount()}/${this.inv.getTransportCapacity()}    🧪 ${this.inv.getConsumables().length}/${this.inv.getMaxConsumables()}`, {
        fontSize: '13px', color: '#f39c12', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
    } else {
      this.add.text(GAME_CX, 65, `💰 ${this.run.getGold()} Or    🎒 ${this.inv.getTransportCount()}/${this.inv.getTransportCapacity()}    🧪 ${this.inv.getConsumables().length}/${this.inv.getMaxConsumables()}`, {
        fontSize: '14px', color: '#f39c12', fontFamily: FONT_BODY,
      }).setOrigin(0.5);
    }

    // Dialogue marchand
    this.add.text(GAME_CX, _mobShop ? 105 : 100, '"Bienvenue moussaillon... J\'ai c\'qu\'il te faut."', {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
    }).setOrigin(0.5);

    // Items à vendre
    let y = 145;
    for (const item of this.shopItems) {
      this.drawShopItem(item, y);
      y += 75;
    }

    // Consommable à vendre
    this.drawShopConsumable(y);
    y += 75;

    // Option soin
    this.drawHealOption(y + 10);

    // Boutons du bas (responsive)
    const btnY = GAME_H - 60;
    const equipBtn = this.add.text(GAME_CX - Math.round(GAME_W * 0.22), btnY, '[ ⚓ Équiper ]', {
      fontSize: '14px', color: '#3498db', fontFamily: FONT_BODY,
      backgroundColor: '#1a1a2e', padding: { x: 12, y: 8 },
    }).setOrigin(0.5);
    equipBtn.setInteractive({ useHandCursor: true });
    equipBtn.on('pointerdown', () => {
      startSceneWithFade(this, 'EquipScene', { run: this.run, returnTo: 'ShopScene', returnData: { shopItems: this.shopItems } });
    });
    equipBtn.on('pointerover', () => equipBtn.setColor('#2980b9'));
    equipBtn.on('pointerout', () => equipBtn.setColor('#3498db'));

    const leaveBtn = this.add.text(GAME_CX + Math.round(GAME_W * 0.22), btnY, '[ 🗺️ Quitter ]', {
      fontSize: '14px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 12, y: 8 },
    }).setOrigin(0.5);
    leaveBtn.setInteractive({ useHandCursor: true });
    leaveBtn.on('pointerdown', () => {
      this.run.completeCurrentNode();
      startSceneWithFade(this, 'MapScene', { run: this.run });
    });
    leaveBtn.on('pointerover', () => leaveBtn.setColor('#ff6b6b'));
    leaveBtn.on('pointerout', () => leaveBtn.setColor('#e74c3c'));
  }

  private drawShopItem(item: ItemDefinition, y: number): void {
    const cardW = Math.min(560, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const price = PRICES[item.rarity] ?? 15;
    const hasBagSpace = this.inv.hasTransportSpace();
    const canAfford = this.run.getGold() >= price && hasBagSpace;
    const rarityColor = RARITY_COLORS[item.rarity] ?? '#ffffff';

    // Fond de l'item
    this.add.rectangle(GAME_CX, y + 25, cardW, 60, 0x1e2d4a, canAfford ? 1 : 0.4);

    // Info item — LOT C4 : on prend juste l'emoji du slot pour éviter
    // les noms dupliqués type "💍 Anneau Anneau d'Abîme". L'emoji suffit
    // à indiquer le type (le mot est répété dans le nom dans 80% des cas).
    const slotLabel = SLOT_LABELS[item.slot] ?? item.slot;
    const slotEmoji = slotLabel.split(' ')[0];  // "💍 Anneau" → "💍"
    this.add.text(lx, y + 10, `${slotEmoji} ${item.name}`, {
      fontSize: '14px', color: rarityColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    });

    this.add.text(lx, y + 30, item.description, {
      fontSize: '11px', color: '#95a5a6', fontFamily: FONT_BODY,
      wordWrap: { width: cardW * 0.6 },
    });

    // Prix + bouton acheter (colonne droite)
    const priceColor = canAfford ? '#f39c12' : '#7f8c8d';
    this.add.text(rx, y + 12, `💰 ${price}`, {
      fontSize: '13px', color: priceColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(1, 0);

    // Bouton acheter
    if (canAfford) {
      const buyBtn = this.add.text(rx, y + 34, '[ Acheter ]', {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a3a2a', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerdown', () => {
        this.sfx.select();
        this.run.addGold(-price);
        this.inv.pickLootItem(item.id);
        this.run.getMeta().addStat('itemsLooted');
        // Retirer l'item de la boutique
        this.shopItems = this.shopItems.filter(i => i.id !== item.id);
        this.drawUI();
      });
      buyBtn.on('pointerover', () => buyBtn.setColor('#ffffff'));
      buyBtn.on('pointerout', () => buyBtn.setColor('#2ecc71'));
      ensureTouchTarget(buyBtn);
    }
  }

  private drawShopConsumable(y: number): void {
    const cardW = Math.min(560, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const pool = [...CONSUMABLE_LOOT.common, ...CONSUMABLE_LOOT.uncommon];
    const consId = pool[Math.floor(Math.random() * pool.length)];
    const def = CONSUMABLE_DEFINITIONS[consId];
    if (!def) return;
    const price = def.rarity === 'common' ? 8 : 15;
    const consFull = this.inv.getConsumables().length >= this.inv.getMaxConsumables();
    const canAfford = this.run.getGold() >= price && !consFull;
    const rarityColor = RARITY_COLORS[def.rarity] ?? '#ffffff';

    this.add.rectangle(GAME_CX, y + 25, cardW, 60, 0x2d1e4a, canAfford ? 1 : 0.4);

    this.add.text(lx, y + 10, `🧪  ${def.name}`, {
      fontSize: '14px', color: rarityColor, fontFamily: FONT_BODY, fontStyle: 'bold',
    });
    this.add.text(lx, y + 30, def.description, {
      fontSize: '11px', color: '#95a5a6', fontFamily: FONT_BODY,
      wordWrap: { width: cardW * 0.6 },
    });
    this.add.text(rx, y + 12, `💰 ${price}`, {
      fontSize: '13px', color: canAfford ? '#f39c12' : '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(1, 0);

    if (canAfford) {
      const buyBtn = this.add.text(rx, y + 34, '[ Acheter ]', {
        fontSize: '11px', color: '#9b59b6', fontFamily: FONT_BODY,
        backgroundColor: '#1a1a3a', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      buyBtn.setInteractive({ useHandCursor: true });
      buyBtn.on('pointerdown', () => {
        this.sfx.select();
        this.run.addGold(-price);
        this.inv.addConsumable(consId);
        this.drawUI();
      });
      buyBtn.on('pointerover', () => buyBtn.setColor('#c39bd3'));
      buyBtn.on('pointerout', () => buyBtn.setColor('#9b59b6'));
      ensureTouchTarget(buyBtn);
    }
  }

  private drawHealOption(y: number): void {
    const cardW = Math.min(560, GAME_W - 20);
    const lx = Math.round(GAME_CX - cardW / 2 + 10);
    const rx = Math.round(GAME_CX + cardW / 2 - 10);
    const hp = this.run.getPlayerHp();
    const maxHp = this.run.getPlayerMaxHp();
    const isFullHp = hp >= maxHp;
    const canAfford = this.run.getGold() >= HEAL_COST && !isFullHp;

    this.add.rectangle(GAME_CX, y + 25, cardW, 55, 0x1a2a1a, canAfford ? 1 : 0.3);

    const healAmount = Math.floor(maxHp * HEAL_PERCENT);
    this.add.text(lx, y + 12, `❤️ Soins (+${healAmount} HP)`, {
      fontSize: '14px', color: canAfford ? '#2ecc71' : '#7f8c8d', fontFamily: FONT_BODY,
    });

    this.add.text(lx, y + 32, isFullHp ? 'HP au maximum' : `HP actuel: ${hp}/${maxHp}`, {
      fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
    });

    this.add.text(rx, y + 12, `💰 ${HEAL_COST}`, {
      fontSize: '13px', color: canAfford ? '#f39c12' : '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(1, 0);

    if (canAfford) {
      const healBtn = this.add.text(rx, y + 34, '[ Soigner ]', {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
        backgroundColor: '#1a3a2a', padding: { x: 6, y: 3 },
      }).setOrigin(1, 0);
      healBtn.setInteractive({ useHandCursor: true });
      healBtn.on('pointerdown', () => {
        this.sfx.select();
        this.run.addGold(-HEAL_COST);
        // Soigner tous les membres vivants de l'équipe
        this.run.healAliveTeam(HEAL_PERCENT);
        this.drawUI();
      });
      healBtn.on('pointerover', () => healBtn.setColor('#ffffff'));
      healBtn.on('pointerout', () => healBtn.setColor('#2ecc71'));
    }
  }
}
